import React, { useEffect, useRef, useState } from 'react';
import { COLORS, LAYOUT_SIZES, Z_INDEXES } from '@constants';
import { Icon } from '@ui';
import { formatString } from '@utils';
import { Flex, Tooltip as AntdTooltip } from 'antd';
import { useSubscriptionOrderModal } from '../../../../hooks/useSubscriptionOrderModal';
import { getCommissionData } from '../../utils/getCommissionData';
import { getSubscriptionOrderLabel } from '../../utils/getSubscriptionOrderLabel';
import { CommissionIcon } from '../CommissionIcon/styled';
import { SUBSCRIPTION_ORDERS_FAQ_PATH, TOOLTIP_PLACEMENTS, TOOLTIP_SIZES } from './constants';
import {
    StyledCloseButton,
    StyledFlex,
    StyledGrayBottomFlex,
    StyledHeader,
    StyledLinkButton,
    StyledName,
    StyledNavLink,
    StyledTriggerButton,
    StyledValue
} from './styled';

export const CommissionTooltip = ({
    text,
    trigger = 'hover',
    subscriptionOrderPosition,
    metrics,
    orderItems,
    subscriptionOriginOrderUUID,
    ...props
}) => {
    const [open, setOpen] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [placement, setPlacement] = useState(TOOLTIP_PLACEMENTS.ABOVE_TRIGGER);
    const overlayRef = useRef(null);
    const triggerRef = useRef(null);
    const { openModal } = useSubscriptionOrderModal();

    const close = () => {
        setIsPinned(false);
        setOpen(false);
    };

    const resolvePlacement = () => {
        const triggerTop = triggerRef.current?.getBoundingClientRect().top ?? 0;
        const spaceUnderHeader = triggerTop - LAYOUT_SIZES.HEADER_HEIGHT - TOOLTIP_SIZES.ARROW_OFFSET;
        const hasRoomAboveTrigger = spaceUnderHeader >= TOOLTIP_SIZES.MAX_HEIGHT;
        return hasRoomAboveTrigger ? TOOLTIP_PLACEMENTS.ABOVE_TRIGGER : TOOLTIP_PLACEMENTS.BELOW_TRIGGER;
    };

    useEffect(() => {
        if (!isPinned) return undefined;
        const handleDocumentClick = ({ target }) => {
            if (overlayRef.current?.contains(target)) return;
            close();
        };

        document.addEventListener('click', handleDocumentClick);

        return () => document.removeEventListener('click', handleDocumentClick);
    }, [isPinned]);

    const handleOpenChange = (nextOpen) => {
        if (isPinned) return;
        if (nextOpen) setPlacement(resolvePlacement());

        setOpen(nextOpen);
    };

    const handleTriggerClick = (event) => {
        event.stopPropagation();

        if (isPinned) {
            close();
            return;
        }

        setPlacement(resolvePlacement());
        setIsPinned(true);
        setOpen(true);
    };

    const handleOriginOrderClick = () => {
        close();
        openModal(subscriptionOriginOrderUUID);
    };

    const commissionData = getCommissionData(
        metrics.commissionTypeName,
        orderItems,
        metrics.isCommissionLimitedToSubtotal
    );
    const commissionType = {
        name: 'Commission type:',
        value: commissionData.type
    };
    const rows = [
        { name: 'Total engagements (last 90 days):', value: metrics.totalEngagements },
        {
            name: 'Days to conversion:',
            value: metrics.daysToConversionToDisplay
        },
        { name: 'Last engagement:', value: metrics.lastEngagement }
    ];

    const canOpenOriginOrder = Boolean(subscriptionOriginOrderUUID);
    const title = (
        <div ref={overlayRef} data-e2e='orders_commission-tooltip_content'>
            <Flex color={COLORS.$white} vertical>
                <Flex align='center' justify='space-between'>
                    <StyledHeader data-e2e='orders_commission-tooltip_title' color={COLORS.$white} type='headline3'>
                        Engagement details
                    </StyledHeader>
                    {isPinned ? (
                        <StyledCloseButton
                            data-e2e='orders_commission-tooltip_close'
                            type='button'
                            aria-label='Close engagement details'
                            onClick={close}
                        >
                            <Icon size='16px' color={COLORS.$white} component={() => <Icon.CloseIcon />} />
                        </StyledCloseButton>
                    ) : null}
                </Flex>
                {rows.map((row, index) => (
                    <StyledGrayBottomFlex key={index} align='center' justify='space-between'>
                        <StyledName
                            data-e2e={`orders_tooltip-${formatString(row.name)}_row`}
                            color={COLORS.$gray60Black}
                            type='body2'
                        >
                            {row.name}
                        </StyledName>
                        <StyledValue
                            data-e2e={`orders_tooltip-${formatString(row.name)}_value`}
                            color={COLORS.$white}
                            type='body2'
                        >
                            {row.value}
                        </StyledValue>
                    </StyledGrayBottomFlex>
                ))}
                {subscriptionOrderPosition ? (
                    <React.Fragment>
                        <StyledGrayBottomFlex align='center' justify='space-between'>
                            <StyledName
                                data-e2e='orders_tooltip-commission-type_row'
                                color={COLORS.$gray60Black}
                                type='body2'
                            >
                                {commissionType.name}
                            </StyledName>
                            <StyledValue
                                data-e2e='orders_tooltip-commission-type_value'
                                color={COLORS.$white}
                                type='body2'
                            >
                                {commissionType.value}
                            </StyledValue>
                        </StyledGrayBottomFlex>
                        <StyledGrayBottomFlex align='center' justify='space-between'>
                            <StyledName
                                data-e2e='orders_tooltip-subscription-order_row'
                                color={COLORS.$gray60Black}
                                type='body2'
                            >
                                Subscription order*
                            </StyledName>
                            <StyledValue
                                data-e2e='orders_tooltip-subscription-order_value'
                                color={COLORS.$white}
                                type='body2'
                            >
                                {getSubscriptionOrderLabel(subscriptionOrderPosition)}
                            </StyledValue>
                        </StyledGrayBottomFlex>
                        <StyledFlex align='center' justify='space-between'>
                            <StyledName
                                data-e2e='orders_tooltip-subscription-order_description'
                                color={COLORS.$white}
                                type='body2'
                            >
                                *Klickly bills only for{' '}
                                {canOpenOriginOrder ? (
                                    <StyledLinkButton
                                        data-e2e='orders_tooltip-subscription-origin-order_link'
                                        type='button'
                                        onClick={handleOriginOrderClick}
                                    >
                                        1st
                                    </StyledLinkButton>
                                ) : (
                                    '1st'
                                )}{' '}
                                and 2nd subscription orders per customer.{' '}
                                <StyledNavLink
                                    data-e2e='orders_tooltip-subscription-order_learn-more'
                                    to={SUBSCRIPTION_ORDERS_FAQ_PATH}
                                    target='_blank'
                                >
                                    Learn more
                                </StyledNavLink>
                            </StyledName>
                        </StyledFlex>
                    </React.Fragment>
                ) : (
                    <StyledFlex align='center' justify='space-between'>
                        <StyledName
                            data-e2e='orders_tooltip-commission-type_row'
                            $widen={commissionData.widen}
                            color={COLORS.$gray60Black}
                            type='body2'
                        >
                            {commissionType.name}
                        </StyledName>
                        <StyledValue data-e2e='orders_tooltip-commission-type_value' color={COLORS.$white} type='body2'>
                            {commissionType.value}
                        </StyledValue>
                    </StyledFlex>
                )}
            </Flex>
        </div>
    );

    return (
        <AntdTooltip
            overlayStyle={{
                minWidth: TOOLTIP_SIZES.MIN_WIDTH,
                ...(commissionData.widen ? { maxWidth: TOOLTIP_SIZES.WIDE_MAX_WIDTH } : {}),
                minHeight: TOOLTIP_SIZES.MIN_HEIGHT,
                maxHeight: TOOLTIP_SIZES.MAX_HEIGHT,
                borderRadius: TOOLTIP_SIZES.BORDER_RADIUS
            }}
            zIndex={Z_INDEXES.TOOLTIP}
            title={title}
            trigger={trigger}
            placement={placement}
            open={open}
            onOpenChange={handleOpenChange}
            {...props}
        >
            <StyledTriggerButton
                ref={triggerRef}
                type='button'
                aria-label='Show engagement details'
                onClick={handleTriggerClick}
            >
                <Icon size='16px' component={() => <CommissionIcon color={COLORS.$gray40Black} />} />
            </StyledTriggerButton>
        </AntdTooltip>
    );
};
